import { useState } from 'react'
import { Message, MessageStatus } from '../services/api'

interface MediaData {
  type: 'image' | 'video'
  url: string
  id?: string
  name?: string
  size?: number
}

interface ChatBubbleProps {
  message: Message
  isSent: boolean
  formatTime: (timestamp: string) => string
}

// Parse media message
const parseMediaMessage = (text: string): MediaData | null => {
  if (!text.startsWith('[MEDIA]')) return null
  try {
    const json = text.slice(7) // Remove "[MEDIA]" prefix
    return JSON.parse(json)
  } catch {
    return null
  }
}

export default function ChatBubble({ message, isSent, formatTime }: ChatBubbleProps) {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [mediaLoaded, setMediaLoaded] = useState(false)

  const mediaData = parseMediaMessage(message.text)
  const getStatusIcon = (status: MessageStatus) => {
    switch (status) {
      case 'sending':
        return (
          <div className="w-3 h-3 border border-white/50 border-t-white rounded-full animate-spin" />
        )
      case 'sent':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )
      case 'delivered':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'read':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'failed':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      default:
        return null
    }
  }

  return (
    <div
      className={`flex ${isSent ? 'justify-end' : 'justify-start'} animate-fade-in`}
    >
      <div
        className={`
          ${isSent ? 'chat-bubble-sent ml-16' : 'chat-bubble-received mr-16'}
          ${message.status === 'failed' ? 'border-l-2 border-error' : ''}
          relative group
        `}
      >
        {/* Message Content */}
        {mediaData ? (
          // Media message
          <div className="relative">
            {!mediaLoaded && (
              <div className="w-48 h-32 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <div className="spinner border-primary border-t-primary-light" />
              </div>
            )}

            {mediaData.type === 'image' ? (
              <img
                src={mediaData.url}
                alt={mediaData.name || 'Image'}
                className={`max-w-xs rounded-lg cursor-pointer ${mediaLoaded ? '' : 'hidden'}`}
                onLoad={() => setMediaLoaded(true)}
                onClick={() => setIsFullscreen(true)}
              />
            ) : (
              <video
                src={mediaData.url}
                className={`max-w-xs rounded-lg ${mediaLoaded ? '' : 'hidden'}`}
                onLoadedData={() => setMediaLoaded(true)}
                controls
              />
            )}

            {/* Fullscreen modal */}
            {isFullscreen && (
              <div
                className="fixed inset-0 z-50 bg-black flex items-center justify-center"
                onClick={() => setIsFullscreen(false)}
              >
                <button
                  className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full z-50"
                  onClick={() => setIsFullscreen(false)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {mediaData.type === 'image' ? (
                  <img
                    src={mediaData.url}
                    alt={mediaData.name || 'Image'}
                    className="max-w-full max-h-full object-contain"
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <video
                    src={mediaData.url}
                    className="max-w-full max-h-full"
                    controls
                    autoPlay
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </div>
            )}
          </div>
        ) : (
          // Text message
          <p className="whitespace-pre-wrap break-words">{message.text}</p>
        )}

        {/* Time and Status */}
        <div className={`flex items-center gap-1 mt-1 ${isSent ? 'justify-end' : ''}`}>
          <span className={`text-xs ${isSent ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
            {formatTime(message.timestamp)}
          </span>
          {isSent && getStatusIcon(message.status)}
        </div>

        {/* Failed message retry button */}
        {message.status === 'failed' && (
          <button
            className="absolute -right-8 top-1/2 -translate-y-1/2 p-1
                       text-error hover:bg-error/10 rounded-full
                       opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => {
              // TODO: Implement retry logic
              console.log('Retry sending message:', message.id)
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}

// Additional chat-related components

interface TypingIndicatorProps {
  username: string
}

export function TypingIndicator({ username }: TypingIndicatorProps) {
  return (
    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm px-4 py-2">
      <div className="flex gap-1">
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
      <span>{username} is typing...</span>
    </div>
  )
}

interface DateDividerProps {
  date: string
}

export function DateDivider({ date }: DateDividerProps) {
  return (
    <div className="flex items-center justify-center my-4">
      <span className="bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs px-3 py-1 rounded-full">
        {date}
      </span>
    </div>
  )
}

interface SystemMessageProps {
  text: string
}

export function SystemMessage({ text }: SystemMessageProps) {
  return (
    <div className="flex items-center justify-center my-2">
      <div className="bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-xs px-4 py-2 rounded-xl text-center max-w-xs">
        {text}
      </div>
    </div>
  )
}

interface EncryptionBadgeProps {
  recipientName?: string
}

export function EncryptionBadge({ recipientName: _recipientName }: EncryptionBadgeProps) {
  return (
    <div className="flex flex-col items-center justify-center my-8 text-center">
      <div className="w-16 h-16 bg-secure-green/10 rounded-full flex items-center justify-center mb-3">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-secure-green" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <p className="text-secure-green text-sm font-medium mb-1">
        Messages are end-to-end encrypted
      </p>
      <p className="text-gray-500 dark:text-gray-400 text-xs max-w-xs">
        No one outside of this chat, not even LYNKSTAR, can read or listen to them.
      </p>
    </div>
  )
}
